import { useState, useEffect } from "react";
import { Mail, UserPlus, Trash2, Loader2, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

interface Invite {
  id: string;
  email: string;
  accepted: boolean;
  created_at: string;
  expires_at: string;
}

interface AdminUser {
  id: string;
  user_id: string;
  role: string;
  email?: string;
}

const SUPER_ADMIN_EMAIL = "song@yourmomentsong.com";

const AdminInvitePanel = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [invites, setInvites] = useState<Invite[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;

  const fetchData = async () => {
    const [inviteRes, adminRes] = await Promise.all([
      supabase.from("admin_invites").select("*").order("created_at", { ascending: false }),
      supabase.functions.invoke("get-admin-emails"),
    ]);
    if (inviteRes.data) setInvites(inviteRes.data);
    if (adminRes.data && Array.isArray(adminRes.data)) {
      setAdmins(adminRes.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInvite = async () => {
    if (!email.trim() || !user) return;
    setSending(true);

    const { error } = await supabase.from("admin_invites").insert({
      email: email.trim().toLowerCase(),
      invited_by: user.id,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Invite sent", description: `Invited ${email} as admin.` });
      setEmail("");
      fetchData();
    }
    setSending(false);
  };

  const handleDelete = async (id: string) => {
    await supabase.from("admin_invites").delete().eq("id", id);
    fetchData();
  };

  const handleRevokeAdmin = async (adminRoleId: string, adminUserId: string) => {
    // Prevent revoking super admin
    if (adminUserId === user?.id) return;
    setRevoking(adminRoleId);
    const { error } = await supabase.from("user_roles").delete().eq("id", adminRoleId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Admin revoked", description: "Admin access has been removed." });
      fetchData();
    }
    setRevoking(null);
  };

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Invite Form */}
      <div className="bg-card rounded-2xl shadow-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Invite Admin</h3>
        <div className="flex gap-3">
          <Input
            type="email"
            placeholder="admin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1"
            aria-label="Admin email address to invite"
          />
          <Button onClick={handleInvite} disabled={sending || !email.trim()}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Invite
          </Button>
        </div>
      </div>

      {/* Current Admins */}
      <div className="bg-card rounded-2xl shadow-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Current Admins ({admins.length})</h3>
        <div className="divide-y divide-border">
          {admins.map((admin) => {
            const isCurrentUser = admin.user_id === user?.id;
            const isSuperAdminUser = isCurrentUser && isSuperAdmin;
            return (
              <div key={admin.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <div>
                    <span className="text-sm text-foreground">{admin.email || admin.user_id.slice(0, 8) + "…"}</span>
                    {isSuperAdminUser && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Super Admin</span>
                    )}
                    {isCurrentUser && !isSuperAdminUser && (
                      <span className="ml-2 text-xs text-muted-foreground">(You)</span>
                    )}
                  </div>
                </div>
                {isSuperAdmin && !isCurrentUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeAdmin(admin.id, admin.user_id)}
                    disabled={revoking === admin.id}
                  >
                    {revoking === admin.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-destructive" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-destructive" />
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div className="bg-card rounded-2xl shadow-soft p-6">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Invites</h3>
          <div className="divide-y divide-border">
            {invites.map((invite) => (
              <div key={invite.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {invite.accepted ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-accent" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-foreground">{invite.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {invite.accepted ? "Accepted" : `Expires ${new Date(invite.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                {!invite.accepted && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(invite.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvitePanel;
